import { Button } from "@/components/ui/button";
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { TFunction } from "i18next";
import { FieldErrors, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form } from "@/components/ui/form";
import { DateInputField, FormattedNumberInputField } from "@/components/shared/form";
import { CircleLoader } from "@/components/shared/loaders";
import { convertDecimalToBp } from "@/utils/helpers";
import { useEffect } from "react";
import useAddSocialSecurityMutationHook from "@/hooks/mutations/useAddSocialSecurityMutation";

const createSocialSecuritySchema = (t: TFunction) => z.object({
  activeFrom: z.date({
    error: t("schemas:form.required"),
  }),
  value: z.number({
    message: t("schemas:form.required"),
  }).min(0, t("schemas:form.atleast0")).max(100, t("schemas:form.atmax100")),
});

interface AddSocialSecurityDialogProps {
  open: boolean;
  onOpenChange: (_open: boolean) => void;
}

/**
 * AddSocialSecurityDialog component for adding a new social security
 * @param props Component properties
 * @returns JSX element
 */
function AddSocialSecurityDialog({ open, onOpenChange }: AddSocialSecurityDialogProps) {
  const { t } = useTranslation(["modals", "common"]);

  const { addSocialSecurityMutation, loading: createSocialSecurityLoading } = useAddSocialSecurityMutationHook();

  const socialSecuritySchema = createSocialSecuritySchema(t);
  const form = useForm<z.infer<typeof socialSecuritySchema>>({
    resolver: zodResolver(socialSecuritySchema),
    defaultValues: {
      activeFrom: undefined,
      value: undefined,
    },
    mode: "onChange",
  });

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      form.reset({
        activeFrom: undefined,
        value: undefined,
      });
    }
  }, [open, form]);

  const onSubmit = (data: z.infer<typeof socialSecuritySchema>) => {

    // Convert the value from decimal to basis points
    const valueInBp = convertDecimalToBp(data.value);

    const processedData = {
      ...data,
      value: valueInBp
    };

    addSocialSecurityMutation({
      variables: { input: processedData },
    }).then(() => {
      onOpenChange(false);
    });
  };

  const onInvalid = (errors: FieldErrors<z.infer<typeof socialSecuritySchema>>) => {
    console.error(errors);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit, onInvalid)} className="space-y-6" id="add-social-security-form">
            <DialogHeader>
              <DialogTitle>
                {t("socialsecurity.title")}
              </DialogTitle>
              <DialogDescription>
                {t("socialsecurity.description")}
              </DialogDescription>
            </DialogHeader>
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-2">
                <DateInputField
                  form={form}
                  name="activeFrom"
                  label={t("socialsecurity.inputs.activationdate.label")}
                  className="w-full"
                  allowOlderDates={true}
                />
              </div>

              <FormattedNumberInputField
                control={form.control}
                name="value"
                label={t("socialsecurity.inputs.value.label")}
                placeholder={t("socialsecurity.inputs.value.placeholder")}
                className="w-full"
                style="decimal"
                additionalSuffix="%"
                decimalScale={2}
                isStandalone={true}
              />

            </div>
            <DialogFooter>
              <DialogClose asChild>
                <Button variant="outline" type="button">
                  {t("common:buttons.close")}
                </Button>
              </DialogClose>
              <Button type="submit" disabled={createSocialSecurityLoading}>
                <CircleLoader visible={createSocialSecurityLoading} height={16} width={16} strokeWidth={2} />
                {t("common:buttons.submit")}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

export default AddSocialSecurityDialog;
